import React from 'react';

/**
 * The app's 3D icon set, on the web.
 *
 * <p>Mirrors dofast-ui/src/components/Icon3D.js: same 30 slots, same names,
 * same artwork files, copied to public/icons3d/. Keeping the names identical
 * matters more than it looks — it means a slot added to the app can be added
 * here by copying one PNG, and a call site reads the same in both codebases.
 *
 * <p>The site previously drew these as emoji (🏠, 📊, 🔍 …), which render as a
 * different picture on every platform, sit on a different baseline than the
 * text beside them, and get read aloud by screen readers as their Unicode
 * name. An <img> with an empty alt is decorative, silent, and the same picture
 * everywhere.
 *
 * @param name  slot name, e.g. "flats"
 * @param size  rendered box in px
 * @param label optional accessible name; omit for decorative use, which is the
 *              common case because a visible text label usually sits alongside
 */

export const ICON_NAMES = [
    'ac', 'all', 'atm', 'bell', 'bus-stop', 'chat', 'cook', 'empty-search',
    'flats', 'fridge', 'grocery', 'gym', 'hero-home', 'hospital', 'insights',
    'kitchen', 'location', 'lock', 'metro', 'parking', 'pg', 'pharmacy',
    'power-backup', 'prime', 'restaurant', 'rooms', 'tv', 'verified',
    'washing-machine', 'wifi',
];

const Icon3D = ({ name, size = 32, label, style, className }) => {
    if (!ICON_NAMES.includes(name)) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(`[Icon3D] unknown icon "${name}" — add the PNG to public/icons3d/`);
        }
        return null;
    }

    return (
        <img
            src={`${process.env.PUBLIC_URL || ''}/icons3d/${name}.png`}
            width={size}
            height={size}
            alt={label || ''}
            aria-hidden={label ? undefined : 'true'}
            // Icons below the fold should not compete with the hero for
            // bandwidth, and every one of these is small enough that a late
            // load is invisible.
            loading="lazy"
            decoding="async"
            className={className}
            style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0, ...style }}
        />
    );
};

/**
 * Round tinted badge with an icon in it — the same treatment as the app's
 * CategoryGrid, using the same tint tokens, so a "PG" tile on the site and in
 * the app read as the same object.
 */
export const Icon3DBadge = ({ name, label, size = 54, tint = 'var(--brand-soft)', style }) => (
    <span
        style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: tint,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            ...style,
        }}
    >
        <Icon3D name={name} size={Math.round(size * 0.56)} label={label} />
    </span>
);

export default Icon3D;
